import axios from "axios";
import { API_URL, MIDDLE_URL } from "../../Constant/constant";
import moment from "moment";

// ── Axios instance with interceptors ─────────────────────────────────────────
const apiClient = axios.create({
  baseURL: API_URL + MIDDLE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor — attaches fresh token on every request
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const clientId = localStorage.getItem("selectedClientId");
    if (clientId) {
      config.headers["X-Client-Id"] = clientId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle 401 globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("token");
      // Only redirect if not already on login page
      if (!window.location.pathname.toLowerCase().includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const endpoints = {
  transaction: "/transaction",
  accountMaster: "/accountMaster",
  getCategoryList: "/categoryMaster",
  budgetAnalysis: "/budget",
  iconMaster: "/icon",
  contact: "/contact",
  contactTransaction: "/contact/transaction",
  user: "/user",
};

// ── Transaction APIs ─────────────────────────────────────────────────────────
// Function to make a get transactionList
export const getTransactionList = (filter, startDate, endDate, showAll) => {
  const params = {
    search: JSON.stringify(filter),
    // startDate:moment(startDate).utc().startOf("day"),
    startDate:moment(startDate).utc().startOf("day").add(1, 'day'),
    endDate:moment(endDate).utc().endOf("day"),
    showAll,
  };
  return apiClient.get(endpoints.transaction, { params });
};
// Function to make a create transaction
export const createTransaction = (body) => {
  return apiClient.post(endpoints.transaction, body);
};
// Function to make a update transaction
export const updateTransaction = (queryParams, body) => {
  const queryString = new URLSearchParams(queryParams).toString();
  return apiClient.put(`${endpoints.transaction}?${queryString}`, body);
};
// Function to delete a transaction
export const deleteTransaction = (transactionId) => {
  return apiClient.delete(endpoints.transaction, { params: { transactionId } });
};

// Function to get transaction statement
export const getTransactionStatement = (params) => {
  const { responseType, ...queryParams } = params;
  return apiClient.get(`${endpoints.transaction}/statement`, { params: queryParams, responseType });
};

// Function to get month wise income/expense totals
export const getMonthWiseIncomeExpense = (year) => {
  return apiClient.get(`${endpoints.transaction}/monthWiseIncomeExpenseTotal`, {
    params: { showAll: true, year },
  });
};

// ── Icon APIs ────────────────────────────────────────────────────────────────
// Function to make a get IconList
export const getIconList = (showAll, filter) => {
  const params = {
    showAll,
    search: JSON.stringify(filter),
  };
  return apiClient.get(endpoints.iconMaster, { params });
};

// ── Account APIs ─────────────────────────────────────────────────────────────
//get account list api
export const getAccountList = (filter, showAll = true) => {
  const params = {
    search: JSON.stringify(filter),
    showAll,
  };
  return apiClient.get(endpoints.accountMaster, { params });
};

//update account master api
export const updateAccount = (queryParams, body) => {
  const queryString = new URLSearchParams(queryParams).toString();
  return apiClient.put(`${endpoints.accountMaster}?${queryString}`, body);
};
// create account
export const createAccount = (body) => {
  return apiClient.post(endpoints.accountMaster, body);
};
// delete account
export const deleteAccount = (accountId) => {
  return apiClient.delete(endpoints.accountMaster, { params: { accountId } });
};

// ── Category APIs ────────────────────────────────────────────────────────────
//get category list api
export const getCategoryList = (filter, showAll = true) => {
  const params = {
    search: JSON.stringify(filter),
    showAll,
  };
  return apiClient.get(endpoints.getCategoryList, { params });
};
//create category api
export const createCategory = (body) => {
  return apiClient.post(endpoints.getCategoryList, body);
};
// update category
export const updateCategory = (queryParams, body) => {
  const queryString = new URLSearchParams(queryParams).toString();
  return apiClient.put(`${endpoints.getCategoryList}?${queryString}`, body);
};
// delete category
export const deleteCategory = (categoryId) => {
  return apiClient.delete(endpoints.getCategoryList, { params: { categoryId } });
};

// ── Budget APIs ──────────────────────────────────────────────────────────────
// get budget and analysis list
export const getBudgetAnalysis = (filter, startDate, endDate, showAll) => {
  const params = {
    search: JSON.stringify(filter),
    startDate,
    endDate,
    showAll,
  };
  return apiClient.get(endpoints.budgetAnalysis, { params });
};
// create budget
export const createBudget = (body) => {
  return apiClient.post(endpoints.budgetAnalysis, body);
};
// update budget
export const updateBudget = (queryParams, body) => {
  const queryString = new URLSearchParams(queryParams).toString();
  return apiClient.put(`${endpoints.budgetAnalysis}?${queryString}`, body);
};
// delete budget
export const deleteBudget = (budgetId) => {
  return apiClient.delete(endpoints.budgetAnalysis, { params: { budgetId } });
};

// ── Profile APIs ─────────────────────────────────────────────────────────────
export const getProfile = () => {
  return apiClient.get(`${endpoints.user}/profile`);
};

export const updateProfile = (body) => {
  return apiClient.put(endpoints.user, body);
};

export const changePassword = (body) => {
  return apiClient.put(`${endpoints.user}/change-password`, body);
};

// ── Contact APIs ─────────────────────────────────────────────────────────────
//get contact list api
export const getContactList = (filter, showAll = true) => {
  const params = {
    search: JSON.stringify(filter),
    showAll,
  };
  return apiClient.get(endpoints.contact, { params });
};
//get contact transaction list api
export const getContactTransactionList = (filter, showAll = true) => {
  const params = {
    search: JSON.stringify(filter),
    showAll,
  };
  return apiClient.get(endpoints.contactTransaction, { params });
};

// create contact transaction list api
export const createContactTransaction = (body) => {
  return apiClient.post(endpoints.contactTransaction, body);
};

// create contact api
export const createContact = (body) => {
  return apiClient.post(endpoints.contact, body);
};

// delete contact
export const deleteContact = (contactId) => {
  return apiClient.delete(endpoints.contact, { params: { contactId } });
};

// delete contact transaction
export const deleteContactTransaction = (contactTransactionId) => {
  return apiClient.delete(endpoints.contactTransaction, { params: { contactTransactionId } });
};

// get contact transaction statement api
export const getContactTransactionStatement = (params) => {
  const { responseType, ...queryParams } = params;
  return apiClient.get(`${endpoints.contactTransaction}/statement`, { params: queryParams, responseType });
};

// CA Access Endpoints
export const grantCaAccess = (body) => {
  return apiClient.post(`${endpoints.user}/grant-ca-access`, body);
};

export const getCaList = () => {
  return apiClient.get(`${endpoints.user}/ca-list`);
};

export const getClientList = () => {
  return apiClient.get(`${endpoints.user}/clients`);
};

export const revokeCaAccess = (caId) => {
  return apiClient.delete(`${endpoints.user}/revoke-ca-access`, { params: { caId } });
};
